import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertyApi, ListingDetails } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Save, Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ListingEditorPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Form State
    const [headline, setHeadline] = useState('');
    const [description, setDescription] = useState('');
    const [houseRules, setHouseRules] = useState('');
    const [photos, setPhotos] = useState<string[]>([]);
    const [newPhotoUrl, setNewPhotoUrl] = useState('');

    useEffect(() => {
        if (id) fetchProperty();
    }, [id]);

    const fetchProperty = async () => {
        try {
            setIsLoading(true);
            const res = await propertyApi.getById(id!);
            const listing = res.data.listingDetails;
            if (listing) {
                setHeadline(listing.headline || '');
                setDescription(listing.description || '');
                setHouseRules(listing.houseRules || '');
                setPhotos(listing.photos || []);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load listing details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!id) return;
        try {
            setIsSaving(true);
            const listingDetails: ListingDetails = {
                headline,
                description,
                houseRules,
                photos
            };

            // We need to update the property with specific listing details
            // Assuming existing update endpoint handles partial updates including listingDetails
            await propertyApi.update(id, { listingDetails });

            toast.success('Listing details saved successfully');
            navigate(`/dashboard/properties/${id}`);
        } catch (error) {
            console.error(error);
            toast.error('Failed to save listing details');
        } finally {
            setIsSaving(false);
        }
    };

    const addPhoto = () => {
        if (!newPhotoUrl) return;
        setPhotos([...photos, newPhotoUrl]);
        setNewPhotoUrl('');
    };

    const removePhoto = (index: number) => {
        setPhotos(photos.filter((_, i) => i !== index));
    };

    if (isLoading) {
        return <div className="flex justify-center p-12">Loading...</div>;
    }

    return (
        <div className="mx-auto max-w-4xl space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/dashboard/properties/${id}`)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                    </Button>
                    <h1 className="text-2xl font-bold text-gray-900">Edit Listing</h1>
                </div>
                <Button onClick={handleSave} disabled={isSaving}>
                    <Save className="mr-2 h-4 w-4" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
                {/* Main Content */}
                <div className="space-y-6 md:col-span-2">
                    {/* Basics */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <h2 className="mb-4 text-lg font-semibold text-gray-900">Listing Basics</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Headline</label>
                                <input
                                    type="text"
                                    value={headline}
                                    onChange={(e) => setHeadline(e.target.value)}
                                    placeholder="e.g. Cozy Cottage near Downtown"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    rows={6}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Describe your property..."
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* House Rules */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <h2 className="mb-4 text-lg font-semibold text-gray-900">House Rules</h2>
                        <textarea
                            rows={4}
                            value={houseRules}
                            onChange={(e) => setHouseRules(e.target.value)}
                            placeholder="e.g. No smoking, No pets, Quiet hours after 10 PM..."
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                    </div>
                </div>

                {/* Sidebar / Photos */}
                <div className="space-y-6">
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <h2 className="mb-4 text-lg font-semibold text-gray-900">Photos</h2>

                        <div className="mb-4 flex gap-2">
                            <input
                                type="text"
                                value={newPhotoUrl}
                                onChange={(e) => setNewPhotoUrl(e.target.value)}
                                placeholder="Image URL..."
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs"
                            />
                            <Button size="sm" onClick={addPhoto} disabled={!newPhotoUrl}>
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            {photos.map((photo, index) => (
                                <div key={index} className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100">
                                    <img src={photo} alt="Listing" className="h-full w-full object-cover" />
                                    <button
                                        onClick={() => removePhoto(index)}
                                        className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity hover:bg-red-500 group-hover:opacity-100"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                            {photos.length === 0 && (
                                <div className="col-span-2 flex aspect-video items-center justify-center rounded-lg border-2 border-dashed border-gray-200 text-gray-400">
                                    <div className="text-center">
                                        <ImageIcon className="mx-auto mb-1 h-8 w-8 opacity-50" />
                                        <span className="text-xs">No photos added</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
}
